import { motion } from "framer-motion";
import { useGameStore } from "../store";
import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { GameRenderer } from "./GameRenderer";
import { PlayerList } from "./PlayerList";
import { useSearchParams } from "react-router-dom";

const RETURN_DELAY = 30;

export function SpectatorView() {
  const {
    joinAsSpectator,
    gameStarted,
    gameCompleted,
    roomCode,
    isSpectator,
    isSynced,
  } = useGameStore();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [returnCountdown, setReturnCountdown] = useState<number | null>(null);
  const [searchParams] = useSearchParams();

  // SpectatorView : si on arrive avec ?room=CODE, on tente automatiquement de rejoindre
  // en tant que spectateur (joinAsSpectator). Le serveur renverra roomJoined et la liste
  // des players ; si la partie est déjà démarrée, on affiche la scène directement.
  useEffect(() => {
    const roomFromUrl = searchParams.get("room");
    // Rejoindre en spectateur si on a un code dans l'URL et qu'on n'est pas déjà spectateur.
    // Le cas AutoRoom : roomCode est déjà set mais isSpectator est false.
    if (roomFromUrl && !isSpectator) {
      handleJoinAsSpectator(roomFromUrl);
    }
  }, [searchParams]);

  const handleJoinAsSpectator = async (roomCode: string) => {
    if (roomCode.trim()) {
      try {
        setError("");
        await joinAsSpectator(roomCode.toUpperCase());
      } catch {
        setError("Failed to join room. Please check the room code.");
      }
    }
  };

  // Countdown retour à l'accueil quand la partie est terminée
  useEffect(() => {
    if (!gameCompleted) {
      setReturnCountdown(null);
      return;
    }
    setReturnCountdown(RETURN_DELAY);
    const timer = setInterval(() => {
      setReturnCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(timer);
          // Conserver les paramètres d'URL (renderer, questions, etc.)
          const params = new URLSearchParams(window.location.search);
          params.delete("room"); // retirer le room code expiré
          const qs = params.toString();
          window.location.href = `/auto-room${qs ? `?${qs}` : ""}`;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameCompleted]);

  // Handle 'C' key press event to close the tab
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "C" || event.key === "c") {
        window.close();
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  if (!roomCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8"
        >
          <div className="flex justify-center mb-6">
            <Eye className="w-16 h-16 text-purple-600" />
          </div>

          <h1 className="text-3xl font-bold text-center text-purple-800 mb-8">
            Spectate a Game
          </h1>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Room Code
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter 6-character code"
                maxLength={6}
                pattern="[A-Z0-9]*"
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleJoinAsSpectator(code)}
              disabled={!code.trim() || code.length !== 6}
              className="w-full py-3 px-6 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Join as Spectator
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative font-kawaii overflow-hidden">
      {/* Badge spectateur */}
      <div className="absolute top-4 left-4 z-50 bg-white/80 backdrop-blur-xl px-4 py-2 rounded-full shadow-kawaii border-2 border-sakura-200/50">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-sakura-400" />
          <span className="font-bold text-sakura-500 text-sm">
            Spectating: {roomCode}
          </span>
        </div>
      </div>

      {/* Overlay de synchronisation */}
      {isSpectator && !isSynced && (
        <div className="absolute inset-0 z-60 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white/90 backdrop-blur-xl p-6 rounded-blob shadow-kawaii-lg text-center border-2 border-sakura-200/50">
            <p className="text-lg font-extrabold text-sakura-500 mb-2">
              Syncing...
            </p>
            <p className="text-sm text-lavender-400 font-bold">
              Getting player positions
            </p>
          </div>
        </div>
      )}

      {/* Message retour à l'accueil après fin de partie */}
      {gameCompleted && returnCountdown !== null && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 bg-black/70 backdrop-blur-xl px-8 py-4 rounded-full shadow-kawaii-lg border-2 border-white/20"
        >
          <p className="text-white font-black text-lg text-center">
            Retour à l'écran d'accueil dans{" "}
            <span className="text-star text-2xl">{returnCountdown}</span>{" "}
            secondes
          </p>
        </motion.div>
      )}

      {gameStarted ? (
        <>
          <GameRenderer />
          <PlayerList />
        </>
      ) : (
        <div className="h-screen flex items-center justify-center bg-gradient-to-br from-sakura-100 via-lavender-100 to-sky-100">
          <div className="bg-white/80 backdrop-blur-xl p-8 rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50">
            <h2 className="text-2xl font-extrabold text-sakura-500 mb-4 text-center">
              Waiting for Game...
            </h2>
            <PlayerList />
          </div>
        </div>
      )}
    </div>
  );
}
